/**
 * Sandboxes v2 pull-request preview page
 * (`/sandboxes/previews/$applicationId/$prNumber`): status, commit, URLs and
 * logins of one pull request's preview sandbox.
 *
 * @module features/sandboxes/pages/SandboxV2PreviewPage
 */
import React from 'react';
import { Banner, Flex, LoadingOrbit, Page, PageHeader, TagBadge, Text, type TagColor } from '@chatsdet/ui';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useAuth } from '../../../contexts/AuthContext';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { ApiError } from '../../../lib/api-error';
import { SandboxLoginsSection, SandboxUrlsSection } from '../components/v2/detail/SandboxAccessSections';
import { formatRelative } from '../components/overview/time-format';
import { useSandboxV2Preview, type SandboxV2PreviewStatus } from '../hooks/useSandboxV2Preview';

const PREVIEW_STATUS: Record<SandboxV2PreviewStatus, { label: string; color: TagColor }> = {
  queued: { label: 'Queued', color: 'gray' },
  building: { label: 'Building', color: 'blue' },
  ready: { label: 'Ready', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
  destroyed: { label: 'Destroyed', color: 'gray' },
};

export const SandboxV2PreviewPage: React.FC = () => {
  const { applicationId, prNumber } = useParams({ from: '/sandboxes/previews/$applicationId/$prNumber' });
  return <SandboxV2PreviewView key={`${applicationId}-${prNumber}`} applicationId={applicationId} prNumber={Number(prNumber)} />;
};

const SandboxV2PreviewView: React.FC<{ applicationId: string; prNumber: number }> = ({ applicationId, prNumber }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const enabled = Boolean(user?.features?.sandboxV2) && Number.isInteger(prNumber) && prNumber > 0;

  React.useEffect(() => {
    if (user && !user.features?.sandboxV2) void navigate({ to: '/' });
  }, [user, navigate]);

  const query = useSandboxV2Preview(applicationId, prNumber, enabled);
  const preview = query.data;

  useBreadcrumbs([
    { key: 'sandboxes', label: 'Test Environments', onPress: () => navigate({ to: '/sandboxes', search: { view: 'v2' } }) },
    { key: `sandbox-v2-preview-${applicationId}-${prNumber}`, label: `PR #${prNumber}` },
  ]);

  if (!user?.features?.sandboxV2) return null;
  if (query.isLoading) {
    return (
      <Page>
        <Flex justify="center" className="py-24">
          <LoadingOrbit size="lg" />
        </Flex>
      </Page>
    );
  }
  if (query.isError || !preview) {
    const notFound = query.error instanceof ApiError && query.error.code === 'SANDBOX_V2_PREVIEW_NOT_FOUND';
    return (
      <Page>
        <PageHeader title={`PR #${prNumber}`} />
        <Text color="muted" className="mt-4">
          {notFound ? `There is no preview for pull request #${prNumber} in this organization yet.` : (query.error?.message ?? 'Preview not found')}
        </Text>
      </Page>
    );
  }

  const cfg = PREVIEW_STATUS[preview.status];
  const applied = preview.appliedSha ?? preview.headSha;
  const building = preview.appliedSha !== null && preview.appliedSha !== preview.headSha;
  const expiry = preview.leaseExpiresAt ? `Expires ${formatRelative(preview.leaseExpiresAt)}` : null;

  return (
    <Page>
      <PageHeader
        title={`PR #${preview.prNumber}`}
        eyebrow={<TagBadge name={cfg.label} color={cfg.color} size="sm" hideDot />}
        description={
          <span className="flex flex-wrap items-center gap-x-3">
            <span className="font-mono">{applied.slice(0, 12)}</span>
            {building && <span>building <span className="font-mono">{preview.headSha.slice(0, 12)}</span></span>}
            {expiry && <span>{expiry}</span>}
          </span>
        }
      />
      <Flex direction="column" gap="lg" className="mt-4 max-w-4xl">
        {preview.error && (
          <Banner variant="error" className="flex-col !items-stretch">
            <Text weight="semibold">The preview build failed</Text>
            <pre className="max-h-64 overflow-auto font-mono text-xs whitespace-pre-wrap break-all">{preview.error}</pre>
          </Banner>
        )}
        <SandboxUrlsSection portUrls={preview.portUrls} />
        <SandboxLoginsSection personas={preview.personas} />
      </Flex>
    </Page>
  );
};
